/* The floating tab bar.
 *
 * One long scrolling page made everything equally far away: the leveraged book
 * sat below eleven holdings, and dividends below that. Each part is its own
 * screen now, one tap from any other.
 *
 * Value and Holdings used to be two of these. They were halves of one question —
 * what the book is worth, and what it is worth it in — so they share a tab, and
 * the slot they freed went to results, which had been buried inside positions. */

import type { Route } from '../App';

export type TabName = 'value' | 'leverage' | 'earnings' | 'radar' | 'dividends' | 'taxes';

interface Tab {
  name: TabName;
  label: string;
  /* Drawn rather than pulled from an icon font: four small shapes weigh less
     than a dependency, and they inherit the current colour for free. */
  icon: (active: boolean) => JSX.Element;
}

const stroke = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.8, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };

const TABS: Tab[] = [
  {
    name: 'value', label: 'Value',
    icon: () => (
      <svg viewBox="0 0 20 20" width="20" height="20" aria-hidden="true">
        <path d="M2.5 13.5 L7 8.5 L10.5 11.5 L17.5 4.5" {...stroke} />
        <path d="M13.5 4.5 H17.5 V8.5" {...stroke} />
      </svg>
    ),
  },
  {
    name: 'leverage', label: 'Leverage',
    icon: () => (
      <svg viewBox="0 0 20 20" width="20" height="20" aria-hidden="true">
        <path d="M10.5 2.5 L4.5 11 H9.5 L8.5 17.5 L15 9 H10z" {...stroke} />
      </svg>
    ),
  },
  {
    name: 'earnings', label: 'Earnings',
    icon: () => (
      <svg viewBox="0 0 20 20" width="20" height="20" aria-hidden="true">
        <path d="M3.5 16.5 V9.5 M8.5 16.5 V4.5 M13.5 16.5 V11.5" {...stroke} />
        <path d="M2 16.5 H18" {...stroke} />
      </svg>
    ),
  },
  {
    name: 'radar', label: 'Radar',
    icon: () => (
      <svg viewBox="0 0 20 20" width="20" height="20" aria-hidden="true">
        <circle cx="10" cy="10" r="7" {...stroke} />
        <circle cx="10" cy="10" r="3" {...stroke} />
        <path d="M10 10 L15 5.5" {...stroke} />
      </svg>
    ),
  },
  {
    name: 'dividends', label: 'Dividends',
    icon: () => (
      <svg viewBox="0 0 20 20" width="20" height="20" aria-hidden="true">
        <circle cx="10" cy="10" r="6.8" {...stroke} />
        <path d="M12.2 7.6 A2.6 2.6 0 0 0 10 6.6 C8.6 6.6 7.8 7.4 7.8 8.3 C7.8 10.7 12.2 9.5 12.2 11.9 C12.2 12.8 11.3 13.5 10 13.5 A2.6 2.6 0 0 1 7.8 12.5" {...stroke} />
        <path d="M10 5.2 V14.9" {...stroke} />
      </svg>
    ),
  },
  {
    name: 'taxes', label: 'Taxes',
    icon: () => (
      // A percentage: two figures and the stroke between them.
      <svg viewBox="0 0 20 20" width="20" height="20" aria-hidden="true">
        <path d="M15 5 L5 15" {...stroke} />
        <circle cx="6.6" cy="6.6" r="2.1" {...stroke} />
        <circle cx="13.4" cy="13.4" r="2.1" {...stroke} />
      </svg>
    ),
  },
];

export function TabBar({ current, go }: { current: TabName; go: (route: Route) => void }) {
  return (
    <nav className="tabbar" aria-label="Sections">
      <div className="tabbar-inner">
        {TABS.map((tab) => {
          const active = tab.name === current;
          return (
            <button
              key={tab.name}
              className="tab"
              aria-current={active ? 'page' : undefined}
              onClick={() => go({ name: tab.name } as Route)}
            >
              {tab.icon(active)}
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}
